import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { customerApi } from '../lib/api'
import {
  db,
  debtByCustomer,
  debtOrdersOf,
  payDebt,
  type Customer,
  type PayMethod,
} from '../lib/db'
import { useMediaQuery } from '../lib/hooks'
import { bare } from '../lib/menuImages'
import { cashSuggestions, clock, dayMonth, money } from '../lib/money'
import { isOwner, useApp } from '../lib/store'
import { pushNow } from '../lib/sync'
import { NumPad } from '../components/NumPad'
import { PageHead } from '../components/PageHead'
import { Sheet } from '../components/Sheet'

/**
 * Sổ nợ khách quen.
 *
 * Mỗi quán có vài người được mang đồ về trước rồi trả sau: hàng xóm, mối sỉ, mấy anh công
 * trình bên cạnh. Trước đây nhân viên ghi vào tờ giấy dán ở quầy — tờ giấy rách hoặc thất
 * lạc là không ai chứng minh được gì, và chuyện đó làm mất cả tiền lẫn bạn.
 *
 * Số dư không có chỗ lưu: nó là tổng đơn ghi sổ trừ tổng tiền đã trả, tính lại mỗi lần mở
 * màn hình. Lưu sẵn thì phải nhớ cập nhật ở năm chỗ, quên một chỗ là con số ấy nói dối mãi.
 */

/** Mỗi lần bấm hiện thêm chừng này dòng sổ. Đủ một màn hình, không hơn. */
const PAGE = 15

interface Entry {
  id: string
  at: string
  /** Dương là nợ thêm, âm là đã trả bớt. */
  delta: number
  label: string
  sub: string
  /** Số dư ngay sau dòng này. */
  running: number
}

export function CustomersScreen() {
  const say = useApp((s) => s.say)
  const refreshCatalog = useApp((s) => s.refreshCatalog)
  const owner = isOwner(useApp((s) => s.session?.role))
  const hasPane = useMediaQuery('(min-width: 1180px)')

  const [query, setQuery] = useState('')
  const [editing, setEditing] = useState<Customer | 'new' | null>(null)
  const [pickedId, setPickedId] = useState<string | null>(null)

  const customers = useLiveQuery(() => db.customers.orderBy('name').toArray(), [], [])
  const debts = useLiveQuery(() => debtByCustomer(), [], new Map<string, number>())

  const shown = useMemo(() => {
    const q = bare(query)
    return customers
      .filter((c) => !q || bare(c.name).includes(q) || (c.phone ?? '').includes(q))
      .sort(
        (a, b) => (debts.get(b.id) ?? 0) - (debts.get(a.id) ?? 0) || a.name.localeCompare(b.name),
      )
  }, [customers, debts, query])

  const picked = customers.find((c) => c.id === pickedId) ?? null

  const total = useMemo(
    () => shown.reduce((s, c) => s + Math.max(0, debts.get(c.id) ?? 0), 0),
    [shown, debts],
  )
  const owing = shown.filter((c) => (debts.get(c.id) ?? 0) > 0).length

  return (
    <div className="screen">
      <div className="board tables-board">
        <PageHead
          to="/"
          icon="📒"
          tone="is-blue"
          title="Khách quen"
          sub={owing > 0 ? `${owing} người đang nợ · ${money(total)}đ` : 'Chưa ai nợ đồng nào'}
          right={
            owner ? (
              <button
                type="button"
                className="btn btn-brand btn-sm"
                onClick={() => setEditing('new')}
              >
                + Thêm
              </button>
            ) : undefined
          }
        />

        <div className="tables-split">
          <div className="tables-main">
            {customers.length === 0 ? (
              <div className="empty">
                <div className="empty-icon" aria-hidden="true">
                  📒
                </div>
                <p>
                  Chưa có khách quen nào. Thêm một người rồi ở màn hình thu tiền sẽ có thêm cách
                  "Ghi sổ".
                </p>
              </div>
            ) : (
              <>
                <label className="field">
                  <span className="sr-only">Tìm khách</span>
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Tìm tên hoặc số điện thoại…"
                  />
                </label>

                <div className="list">
                  {shown.map((c) => {
                    const debt = debts.get(c.id) ?? 0
                    const over = c.debtLimit > 0 && debt >= c.debtLimit

                    return (
                      <button
                        key={c.id}
                        type="button"
                        className={`row-btn${hasPane && c.id === pickedId ? ' is-picked' : ''}`}
                        onClick={() => setPickedId(c.id)}
                      >
                        <span className="row-text">
                          <b>{c.name}</b>
                          <small>
                            {c.phone ?? 'Chưa có số điện thoại'}
                            {c.debtLimit > 0 ? ` · trần ${money(c.debtLimit)}đ` : ''}
                          </small>
                        </span>
                        <span className={`row-price num${debt > 0 ? ' is-owing' : ''}`}>
                          {debt > 0 ? `${money(debt)}đ` : '—'}
                        </span>
                        {over && <span className="tag-over">Vượt trần</span>}
                        <span aria-hidden="true">›</span>
                      </button>
                    )
                  })}
                </div>
              </>
            )}
          </div>

          {/*
            Sổ của một người nằm trong khoang bên phải, không nằm trong tấm trượt.
            Khách quen một năm là hàng trăm dòng: ghi sổ, trả bớt, rồi lại ghi sổ. Tấm trượt
            cao bằng nửa màn hình thì cuộn mãi không hết, mà mở ra là che mất danh sách nên
            không so được người này với người kia.
          */}
          <aside className="order-pane" aria-label="Sổ nợ của khách đang chọn">
            {picked ? (
              <CustomerLedger
                key={picked.id}
                customer={picked}
                owner={owner}
                say={say}
                onEdit={() => setEditing(picked)}
              />
            ) : (
              <div className="pane-empty">
                <div className="empty-icon" aria-hidden="true">
                  👆
                </div>
                <p>Chạm vào một người để xem sổ và thu tiền.</p>
              </div>
            )}
          </aside>
        </div>
      </div>

      {/* Màn hẹp không có khoang bên phải, nên sổ mở bằng tấm trượt. */}
      {picked && !hasPane && (
        <Sheet title={picked.name} onClose={() => setPickedId(null)}>
          <CustomerLedger
            key={picked.id}
            customer={picked}
            owner={owner}
            say={say}
            onEdit={() => setEditing(picked)}
            flat
          />
        </Sheet>
      )}

      {editing !== null && (
        <CustomerEditor
          customer={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await refreshCatalog()
          }}
          onDeleted={async () => {
            setEditing(null)
            setPickedId(null)
            await refreshCatalog()
          }}
          say={say}
        />
      )}
    </div>
  )
}

/**
 * Sổ của một người: số dư, rồi từng dòng một theo thứ tự thời gian.
 *
 * Gộp đơn ghi sổ với tiền đã trả vào <b>một</b> danh sách, không tách hai mục. Sổ nợ ngoài
 * đời là một cột: hôm nay nợ thêm, hôm kia trả bớt, và thứ đáng đọc là số dư sau mỗi dòng.
 * Tách làm hai thì muốn biết "hôm ấy còn nợ bao nhiêu" phải nhìn qua nhìn lại rồi tự cộng.
 *
 * Chỉ vẽ mười lăm dòng gần nhất, bấm mới hiện thêm. Khách quen lâu năm có hàng trăm dòng,
 * mà thứ người ta mở ra để xem gần như luôn nằm ở mấy dòng đầu.
 */
function CustomerLedger({
  customer,
  owner,
  say,
  onEdit,
  flat,
}: {
  customer: Customer
  owner: boolean
  say: (m: string) => void
  onEdit: () => void
  flat?: boolean
}) {
  const [limit, setLimit] = useState(PAGE)
  const [paying, setPaying] = useState(false)

  const orders = useLiveQuery(() => debtOrdersOf(customer.id), [customer.id], [])
  const payments = useLiveQuery(
    () => db.debtPayments.where('customerId').equals(customer.id).toArray(),
    [customer.id],
    [],
  )

  /** Trộn hai nguồn, chạy số dư từ dòng cũ nhất lên, rồi lật ngược cho mới nhất lên đầu. */
  const entries = useMemo<Entry[]>(() => {
    const raw = [
      ...orders.map((o) => ({
        id: o.id,
        at: o.paidAt ?? o.openedAt,
        delta: o.total,
        label: 'Ghi sổ',
        sub: o.code,
      })),
      ...payments.map((p) => ({
        id: p.id,
        at: p.paidAt,
        delta: -p.amount,
        label: p.method === 'Transfer' ? 'Trả · chuyển khoản' : 'Trả · tiền mặt',
        sub: p.note ?? '',
      })),
    ].sort((a, b) => a.at.localeCompare(b.at))

    let running = 0
    return raw
      .map((r) => {
        running += r.delta
        return { ...r, running }
      })
      .reverse()
  }, [orders, payments])

  const owed = entries[0]?.running ?? 0

  const body = (
    <>
      <div className={`debt-sum${owed > 0 ? ' is-owing' : ''}`}>
        <span>Còn nợ</span>
        <b className="num">{money(Math.max(0, owed))}đ</b>
        {owed < 0 && <small>Khách đã trả dư {money(-owed)}đ</small>}
        {customer.debtLimit > 0 && <small>Trần {money(customer.debtLimit)}đ</small>}
      </div>

      {entries.length === 0 ? (
        <p className="note wide">Chưa có dòng nào trong sổ của người này.</p>
      ) : (
        <>
          <div className="ledger">
            <div className="ledger-head" role="presentation">
              <span>Lúc</span>
              <span>Việc</span>
              <span>Ghi</span>
              <span>Còn nợ</span>
            </div>

            {entries.slice(0, limit).map((e) => (
              <div key={e.id} className={`ledger-row${e.delta < 0 ? ' is-paid' : ''}`}>
                <span className="ledger-when num">
                  {clock(e.at)}
                  <small>{dayMonth(e.at)}</small>
                </span>
                <span className="ledger-what">
                  <b>{e.label}</b>
                  {e.sub && <small>{e.sub}</small>}
                </span>
                <span className="ledger-delta num">
                  {e.delta > 0 ? '+' : '−'}
                  {money(Math.abs(e.delta))}
                </span>
                <span className="ledger-run num">{money(Math.max(0, e.running))}</span>
              </div>
            ))}
          </div>

          {entries.length > limit && (
            <button
              type="button"
              className="btn btn-ghost wide"
              onClick={() => setLimit((v) => v + PAGE)}
            >
              Xem thêm · còn {entries.length - limit} dòng
            </button>
          )}
        </>
      )}

      {customer.note && <p className="note wide">{customer.note}</p>}
    </>
  )

  return (
    <>
      {!flat && (
        <div className="pane-head">
          <span className="pane-name">{customer.name}</span>
          <span className="pane-seats">{customer.phone ?? 'Chưa có số điện thoại'}</span>
          {owner && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
              Sửa
            </button>
          )}
        </div>
      )}

      {flat ? body : <div className="pane-lines">{body}</div>}

      {owed > 0 && (
        <div className="pane-actions">
          <button type="button" className="btn btn-brand grow" onClick={() => setPaying(true)}>
            Khách trả tiền
          </button>
        </div>
      )}

      {paying && (
        <DebtPaySheet
          customer={customer}
          owed={owed}
          onClose={() => setPaying(false)}
          onDone={() => {
            setPaying(false)
            say(`Đã ghi nhận tiền của ${customer.name}`)
          }}
        />
      )}
    </>
  )
}

/** Thêm hoặc sửa một khách. Cần mạng, vì danh sách này nằm trên máy chủ. */
function CustomerEditor({
  customer,
  onClose,
  onSaved,
  onDeleted,
  say,
}: {
  customer: Customer | null
  onClose: () => void
  onSaved: () => void | Promise<void>
  onDeleted: () => void | Promise<void>
  say: (m: string) => void
}) {
  const [name, setName] = useState(customer?.name ?? '')
  const [phone, setPhone] = useState(customer?.phone ?? '')
  const [note, setNote] = useState(customer?.note ?? '')
  const [limit, setLimit] = useState(customer?.debtLimit ?? 0)
  const [busy, setBusy] = useState(false)

  async function save() {
    if (!name.trim()) {
      say('Chưa có tên khách')
      return
    }

    setBusy(true)
    try {
      await customerApi.save({
        id: customer?.id,
        name: name.trim(),
        phone: phone.trim() || null,
        note: note.trim() || null,
        debtLimit: limit,
      })
      await onSaved()
    } catch (e) {
      say(e instanceof Error ? e.message : 'Cần có mạng để lưu khách quen')
    }
    setBusy(false)
  }

  async function remove() {
    if (!customer) return
    setBusy(true)
    try {
      await customerApi.remove(customer.id)
      await onDeleted()
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa xoá được')
    }
    setBusy(false)
  }

  return (
    <Sheet title={customer ? customer.name : 'Thêm khách quen'} onClose={onClose}>
      <label className="field">
        <span>Tên khách</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus />
      </label>

      <label className="field">
        <span>Số điện thoại</span>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
          inputMode="numeric"
          placeholder="Để gọi khi tới kỳ thu"
        />
      </label>

      {/*
        Trần nợ không chặn, chỉ báo.
        Chặn cứng thì nhân viên gặp khách đứng trước mặt mà máy không cho ghi, và họ sẽ ghi
        ra giấy — lúc ấy quán mất luôn cả sổ. Báo đỏ để họ biết mà hỏi chủ quán thì hơn.
      */}
      <div className="field">
        <span>Trần nợ</span>
        <div className="amount-readout num">{limit > 0 ? money(limit) : 'Không đặt trần'}</div>
        <div className="chip-row">
          {[0, 500_000, 1_000_000, 2_000_000, 5_000_000].map((v) => (
            <button
              key={v}
              type="button"
              className="chip"
              aria-pressed={limit === v}
              onClick={() => setLimit(v)}
            >
              {v === 0 ? 'Không đặt' : money(v)}
            </button>
          ))}
        </div>
        <small className="note wide">
          Vượt trần thì máy vẫn cho ghi sổ, chỉ báo đỏ để nhân viên biết mà hỏi chủ quán.
        </small>
      </div>

      <label className="field">
        <span>Ghi chú</span>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={200}
          placeholder="Mối sỉ, hàng xóm, công ty bên cạnh…"
        />
      </label>

      <div className="sheet-actions">
        {customer && (
          <button
            type="button"
            className="btn btn-danger-ghost"
            onClick={() => void remove()}
            disabled={busy}
          >
            Xoá
          </button>
        )}
        <button
          type="button"
          className="btn btn-brand grow"
          onClick={() => void save()}
          disabled={busy}
        >
          {busy ? 'Đang lưu…' : 'Lưu'}
        </button>
      </div>
    </Sheet>
  )
}

/**
 * Khách đưa tiền trả nợ.
 *
 * Làm đúng như màn hình thu tiền bán hàng, vì đây là cùng một động tác: khách chìa tiền,
 * người bán đếm, thối lại nếu thừa.
 *
 *   · Trả từng phần được. Nợ tám trăm mà đưa năm trăm thì ghi năm trăm, còn ba trăm.
 *   · Đưa dư cũng được. Nợ tám trăm mà chìa tờ một triệu thì quán nhận tám trăm và thối
 *     lại hai trăm — sổ chỉ ghi tám trăm, vì quán chỉ giữ chừng ấy.
 */
function DebtPaySheet({
  customer,
  owed,
  onClose,
  onDone,
}: {
  customer: Customer
  owed: number
  onClose: () => void
  onDone: () => void
}) {
  const [given, setGiven] = useState(owed)
  const [method, setMethod] = useState<PayMethod>('Cash')
  const [busy, setBusy] = useState(false)

  /** Quán chỉ giữ tối đa bằng số nợ. Phần dôi ra là tiền thối lại ngay tại chỗ. */
  const taken = Math.min(given, owed)
  const change = Math.max(0, given - owed)
  const left = owed - taken

  async function save() {
    setBusy(true)
    await payDebt(customer.id, taken, method)
    void pushNow()
    setBusy(false)
    onDone()
  }

  return (
    <Sheet title={`${customer.name} trả tiền`} onClose={onClose}>
      <p className="sheet-note">
        Đang nợ {money(owed)}đ. Trả bao nhiêu ghi bấy nhiêu, không cần trả hết một lần.
      </p>

      <div className="field">
        <span>Khách đưa</span>
        <div className="amount-readout num">{money(given)}</div>
        <div className="chip-row chip-cash">
          {cashSuggestions(owed).map((v) => (
            <button
              key={v}
              type="button"
              className={`chip${v === owed ? ' is-exact' : ''}`}
              aria-pressed={given === v}
              onClick={() => setGiven(v)}
            >
              {v === owed ? 'Trả hết' : money(v)}
            </button>
          ))}
        </div>
      </div>

      {/*
        Hai con số người bán cần ngay sau khi gõ: thối lại bao nhiêu, và còn nợ bao nhiêu.
        Không bắt ai làm phép trừ trong đầu trong lúc khách đứng chờ.
      */}
      {(change > 0 || left > 0) && (
        <div className="debt-math">
          {change > 0 && (
            <div className="debt-math-row is-change">
              <span>Thối lại khách</span>
              <b className="num">{money(change)}đ</b>
            </div>
          )}
          <div className="debt-math-row">
            <span>{left > 0 ? 'Còn nợ lại' : 'Trả xong, hết nợ'}</span>
            <b className="num">{money(left)}đ</b>
          </div>
        </div>
      )}

      <div className="seg">
        <button type="button" aria-pressed={method === 'Cash'} onClick={() => setMethod('Cash')}>
          💵 Tiền mặt
        </button>
        <button
          type="button"
          aria-pressed={method === 'Transfer'}
          onClick={() => setMethod('Transfer')}
        >
          📱 Chuyển khoản
        </button>
      </div>

      <NumPad value={given} onChange={setGiven} />

      <div className="sheet-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Quay lại
        </button>
        <button
          type="button"
          className="btn btn-brand grow"
          disabled={taken <= 0 || busy}
          onClick={() => void save()}
        >
          {busy ? 'Đang ghi…' : `Nhận ${money(taken)}đ`}
        </button>
      </div>
    </Sheet>
  )
}
